import React, { useState, useEffect } from 'react';
import { getRoleMenuAccess, updateRolePermissions } from './services/masterDataService';
import { useUserRoles } from './hooks';
import Toast from "./context/ToastContext";
const UserManagementFinal = () => {
  const { roles, loading: rolesLoading } = useUserRoles();
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
   const BRAND_GREEN = '#0aa375';

  const flattenMenu = (items, level = 0) => {
    let flatList = [];
    items.forEach(item => {
      const getVal = (code) => {
        const p = item.permissions?.find(x => x.code === code);
        return p ? p.value === "1" : false;
      };

      flatList.push({
        ...item,
        level: level,
        display: getVal("CanDisplay"),
        add: getVal("CanAdd"),
        edit: getVal("CanEdit"),
        delete: getVal("CanDelete"),
        exportToExcel: getVal("CanExportToExcel"),
        get: getVal("CanGet")
      });

      if (item.children && item.children.length > 0) {
        flatList = [...flatList, ...flattenMenu(item.children, level + 1)];
      }
    });
    return flatList;
  };

  const handleRoleChange = async (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
    setSearchTerm('');

    if (!roleId) {
      setPermissions([]);
      return;
    }

    setLoading(true);
    try {
      const data = await getRoleMenuAccess(roleId);
      const flatData = flattenMenu(data);
      setPermissions(flatData);
    } catch (err) {
      console.error("Permissions fetch error:", err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (menuId, field) => {
    setPermissions(prev => prev.map(item =>
      item.menuId === menuId ? { ...item, [field]: !item[field] } : item
    ));
  };

  const filteredPermissions = permissions.filter(item =>
    (item.displayName || item.menuName || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const triggerToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const handleSaveConfiguration = async () => {
    if (!selectedRoleId) {
      triggerToast("Please select a role first.", "warning");
      return;
    }
    setIsSaving(true);
    try {
      await updateRolePermissions(selectedRoleId, permissions);
      triggerToast("Configuration saved successfully!", "success");
      setTimeout(() => { window.location.reload(); }, 800);
    } catch (err) {
      triggerToast("Failed to save changes.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleFolder = (menuId) => {
    setExpandedIds(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const visiblePermissions = permissions.filter(item => {
    if (searchTerm.trim() !== "") {
      return (item.displayName || item.menuName || "").toLowerCase().includes(searchTerm.toLowerCase());
    }
    if (item.level === 0) return true;
    return expandedIds.includes(item.parentId);
  });

  const handleToggleAll = (e) => {
    if (e.target.checked) {
      const allParentIds = permissions
        .filter(item => item.hasChildren || item.level === 0)
        .map(item => item.menuId);
      setExpandedIds(allParentIds);
    } else {
      setExpandedIds([]);
    }
  };

  const Spinner = () => (
      <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status">
              <span className="visually-hidden">Loading...</span>
          </div>
      </div>
  );

  const s = {
    container: { padding: '0px', background: '#f4f7f6', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" },
    card: { background: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden', maxWidth: '1400px', margin: '0 auto' },
    filterRow: { padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '1px solid #eee', background: '#fff', flexWrap: 'wrap' },
    select: { padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', maxWidth: '250px' },
    searchInput: { padding: '8px 12px', width: '100%', maxWidth: '320px', borderRadius: '4px', border: '1px solid #ccc', outline: 'none' },
    tableContainer: { maxHeight: '70vh', overflowY: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { position: 'sticky', top: 0, background: '#f8f9fa', padding: '6px 12px', fontWeight: 'bold', fontSize: '0.85rem', borderBottom: '2px solid #dee2e6', zIndex: 10 },
    td: { padding: '4px 15px', borderBottom: '1px solid #efefef', fontSize: '0.9rem' },
    checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: '#0aa375' },
    saveBtn: { background: '#0aa375', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', width: '100%', maxWidth: '300px', display: 'block', margin: '12px auto' },
  };

  return (
    <div style={{ ...s.container, position: 'relative', zIndex: 20 }}>
      <div style={s.card}>
        <div style={s.filterRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontWeight: '600', marginRight: '8px' }}>Role:</label>
              <select style={s.select} value={selectedRoleId} onChange={handleRoleChange}>
                <option value="">-- Select User Role --</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.text}</option>
                ))}
              </select>
            </div>

            {selectedRoleId && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#0aa375' }}
                  checked={expandedIds.length > 0 && expandedIds.length === permissions.filter(p => p.hasChildren || p.level === 0).length}
                  onChange={handleToggleAll}
                />
                Open all menus
              </label>
            )}
          </div>

          {selectedRoleId && (
            <div style={{ marginLeft: 'auto', minWidth: '310px' }}>
              <input
                type="text"
                placeholder=" Search menu name..."
                style={s.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
        </div>
        {loading ? (
          <Spinner />
        ) : permissions.length > 0 ? (
          <>
            <div style={s.tableContainer}>
              <table className="d-none d-md-table" style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, textAlign: 'left', width: '35%' }}>Menu Hierarchy</th>
                    {['Display', 'Add', 'Edit', 'Delete', 'Export', 'Get'].map(h => (
                      <th key={h} style={{ ...s.th, textAlign: 'center' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visiblePermissions.map((item) => {
                    const hasChildren = permissions.some(child => child.parentId === item.menuId);
                    const isExpanded = expandedIds.includes(item.menuId);

                    return (
                      <tr key={item.menuId} style={{ background: item.level === 0 ? '#f0f7f4' : '#fff' }}>
                        <td style={{
                          ...s.td,
                          paddingLeft: `${item.level * 30 + 15}px`,
                          fontWeight: item.level === 0 ? 'bold' : '500'
                        }}>
                          {hasChildren ? (
                            <span
                              onClick={() => toggleFolder(item.menuId)}
                              style={{ cursor: 'pointer', marginRight: '8px', fontSize: '1.2rem' }}
                            >
                              {isExpanded ? '📂' : '📁'}
                            </span>
                          ) : (
                            <span style={{ marginRight: '8px', marginLeft: '4px' }}>┕ </span>
                          )}
                          {item.displayName || item.menuName}
                        </td>

                        {['display', 'add', 'edit', 'delete', 'exportToExcel', 'get'].map(field => (
                          <td key={field} style={{ ...s.td, textAlign: 'center' }}>
                            <input
                              style={s.checkbox}
                              type="checkbox"
                              checked={item[field] || false}
                              onChange={() => togglePermission(item.menuId, field)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Card View */}
              <div className="d-md-none">
                {visiblePermissions.map((item) => {
                  const hasChildren = permissions.some(child => child.parentId === item.menuId);
                  const isExpanded = expandedIds.includes(item.menuId);

                  return (
                    <div key={item.menuId} style={{
                      background: item.level === 0 ? '#f0f7f4' : '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '14px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        {hasChildren ? (
                          <span
                            onClick={() => toggleFolder(item.menuId)}
                            style={{ cursor: 'pointer', marginRight: '10px', fontSize: '1.4rem' }}
                          >
                            {isExpanded ? '📂' : '📁'}
                          </span>
                        ) : (
                          <span style={{ marginRight: '10px' }}>┕</span>
                        )}
                        <strong style={{ fontSize: '15px' }}>{item.displayName || item.menuName}</strong>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {[
                          { label: 'Display', field: 'display' },
                          { label: 'Add', field: 'add' },
                          { label: 'Edit', field: 'edit' },
                          { label: 'Delete', field: 'delete' },
                          { label: 'Export', field: 'exportToExcel' },
                          { label: 'Get', field: 'get' }
                        ].map(({ label, field }) => (
                          <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                            <input
                              type="checkbox"
                              checked={item[field] || false}
                              onChange={() => togglePermission(item.menuId, field)}
                              style={{ width: '20px', height: '20px', accentColor: '#0aa375' }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="d-flex justify-content-center gap-3 mt-2" style={{ overflow: 'hidden' }}>
              <button
                className="btn btn-sm text-white px-4 py-2 fw-semibold"
                style={{ 
                  ...s.saveBtn, 
                  opacity: isSaving ? 0.7 : 1,
                  width: '100%',
                  maxWidth: '300px',
                  margin: '12px auto',
                  display: 'block'
                }}
                disabled={isSaving}
                onClick={handleSaveConfiguration}
              >
                {isSaving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          </>
        ) : selectedRoleId && (
          <div style={{ padding: '50px', textAlign: 'center', color: '#666' }}>No data available for this role.</div>
        )}
      </div>
      <Toast isVisible={toast.show} message={toast.message} type={toast.type}
      />
    </div>
  );
};

export default UserManagementFinal;